// Activity-weighted creator leaderboard. Activity (try-ons, aura, featured)
// dominates the score; likes add a small social booster.
import { useState } from "react";
import { Trophy, Flame, Heart, Sparkles, Crown, Medal } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useCreatorLeaderboard,
  type LeaderboardPeriod,
  type LeaderboardRow,
} from "@/hooks/useCreatorLeaderboard";
import { isNativeApp } from "@/lib/platform";
import { GetTheAppInlineCard } from "@/components/GetTheAppInlineCard";

const PERIODS: Array<{ id: LeaderboardPeriod; label: string }> = [
  { id: "week", label: "This week" },
  { id: "all", label: "All-time" },
];

const RankBadge = ({ rank }: { rank: number }) => {
  if (rank === 1) {
    return (
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-yellow-400 to-amber-600 text-white shadow-soft">
        <Crown className="h-4 w-4" />
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-slate-200 to-slate-400 text-slate-900 shadow-soft">
        <Medal className="h-4 w-4" />
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-orange-400 to-orange-700 text-white shadow-soft">
        <Medal className="h-4 w-4" />
      </span>
    );
  }
  return (
    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-muted text-xs font-bold tabular-nums text-foreground">
      {rank}
    </span>
  );
};

const Row = ({ row, highlight = false }: { row: LeaderboardRow; highlight?: boolean }) => {
  const initial = (row.display_name?.[0] ?? "?").toUpperCase();
  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-2xl border px-3 py-2.5 transition-colors",
        highlight
          ? "border-primary/40 bg-primary/10"
          : "border-border/60 bg-background/50",
      )}
    >
      <RankBadge rank={row.rank} />
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/40 to-accent/30 text-xs font-bold uppercase text-foreground ring-1 ring-white/10">
        {initial}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold">
          {row.display_name}
          {row.is_self && (
            <span className="ml-1.5 rounded-full bg-primary/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary">
              you
            </span>
          )}
        </p>
        <p className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-0.5">
            <Sparkles className="h-3 w-3" />
            {row.tryons + row.aura}
          </span>
          <span className="inline-flex items-center gap-0.5">
            <Crown className="h-3 w-3" />
            {row.featured}
          </span>
          <span className="inline-flex items-center gap-0.5">
            <Heart className="h-3 w-3" />
            {row.likes}
          </span>
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm font-bold tabular-nums">{Math.round(row.score)}</p>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">pts</p>
      </div>
    </li>
  );
};

export const CreatorLeaderboard = ({ className }: { className?: string }) => {
  const [period, setPeriod] = useState<LeaderboardPeriod>("week");
  const { rows: allRows, self, loading } = useCreatorLeaderboard(period);

  // Web (desktop + mobile web) — show only the top 3 then push to the app.
  const web = !isNativeApp();
  const rows = web ? allRows.slice(0, 3) : allRows;

  const showSelfPinned =
    self && self.rank > 0 && !rows.some((r) => r.is_self && r.rank <= 20);

  return (
    <section className={cn("relative", className)} aria-label="Creator leaderboard">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-primary" />
          <h2 className="text-base font-bold tracking-tight">Top creators</h2>
        </div>
        <div className="flex items-center gap-1 rounded-full bg-muted p-0.5">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPeriod(p.id)}
              className={cn(
                "rounded-full px-3 py-1 text-[11px] font-semibold transition-colors",
                period === p.id
                  ? "bg-background text-foreground shadow-soft"
                  : "text-muted-foreground",
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <p className="mb-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Flame className="h-3 w-3 text-primary" />
        Try-ons & features used count most. Likes give a bonus.
      </p>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border/60 bg-muted/30 px-4 py-6 text-center">
          <p className="text-sm font-semibold">Be the first on the board</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Run a try-on or read your Aura to start scoring.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <Row key={r.user_id} row={r} highlight={r.is_self} />
          ))}
          {web && allRows.length > rows.length && (
            <li>
              <GetTheAppInlineCard variant="row" message="Download FitAuraAI - Try On Clothes on Google Play to see the full leaderboard." />
            </li>
          )}
        </ul>
      )}

      {showSelfPinned && self && (
        <>
          <div className="my-3 flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> Your rank{" "}
            <span className="h-px flex-1 bg-border" />
          </div>
          <Row
            row={{
              rank: self.rank,
              user_id: "self",
              display_name: self.display_name,
              score: self.score,
              tryons: self.tryons,
              aura: self.aura,
              likes: self.likes,
              featured: self.featured,
              is_self: true,
            }}
            highlight
          />
        </>
      )}
    </section>
  );
};
